import React = require('react');
import { differenceInDays, isAfter, parse } from 'date-fns';
import { IPolicy } from '../../../../../data/api/models';
import { IconCircle } from '../../../../../components/Icons/IconCircle';
import { StatusBadge } from './Components/StatusBadge';
import { capitalize, filter, find, flow, get, head, last, orderBy } from 'lodash';
import { PolicyCarousel } from './Components/PolicyCarousel';
import AdmIcons from '../../../../../components/Icons/AdmIcons';
import { Button } from 'react-bootstrap';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { IStore } from '../../../../../redux/IStore';
import { push } from 'react-router-redux';
import Repurchase from './Components/Repurchase';
import RenewalPolicy from './Components/RenewPolicy';
import { REGION_MAPPING } from '../../../../../services/quote/models';
import { IAuthState } from '../../../../../services/auth/models';
import { IMtaActions, IMtaState } from '../../../../../services/mta/models';
import { mtaActions } from '../../../../../services/mta/actions';

interface IPolicyCard {
  policy: IPolicy
  auth: IAuthState,
  mta: IMtaState,
  actions: {
    push: (route: string) => void;
    mta: IMtaActions;
  }
}

const PolicyCard = ({ policy, actions, auth, mta }: IPolicyCard) => {

  const hasPolicyRenewal = (): boolean => {
    const renewal = head(orderBy(filter(get(auth, 'customer.renewals'), ['renewingPolicyId', policy.id]), ['version'], ['desc']));
    const statuses = ['renewal', 'renewal invited']
    if (statuses.includes(renewal?.status)) {
      return renewal;
    }
    return false;
  }

  const isAnnual = get(policy, 'metadata.scheme_type', '') === 'annual';
  const expiryDateStr = get(policy, 'expiresAt', '');
  const daysDifference = differenceInDays(parse(expiryDateStr, 'dd/MM/yyyy', new Date()), new Date());
  const startDateStr = get(policy, 'commencesAt', '');
  const isFutureDate = isAfter(parse(startDateStr, 'dd/MM/yyyy', new Date()), new Date());
  const tripDefinition = isAnnual ? 'Annual Multi Trip - ' + get(REGION_MAPPING, get(policy, 'metadata.region')) : 'Single Trip - ' + get(policy, 'metadata.destinations', []).join(', ');
  const coverLevel = get(last(get(policy, 'premiums')), 'scheme.coverLevel', '').toLowerCase()
  const travellerNumber = policy?.travellers?.length || 1;
  const isExpired = policy.status === 'expired';
  const isCancelled = policy.status === 'cancelled';
  const addedOptionNumber = filter(get(policy, 'metadata.options'), (o) => {
    return o === true;
  }).length
  const hasRenewal = hasPolicyRenewal();
  let status = policy.status;

  const coverRenewsText = daysDifference >= 0 ? `Cover renews in ${daysDifference} days` : `Cover renews on ${expiryDateStr}`;
  const coverEndsText = daysDifference >= 0 ? `Cover ends in ${daysDifference} days` : `Cover ends on ${expiryDateStr}`;
  let coverText: any = isAnnual && hasRenewal ? coverRenewsText : coverEndsText

  let iconColor = 'green';

  if (
    policy.renewalStatus === 'auto' &&
    hasRenewal &&
    get(policy, 'activePaymentMethod.id', '') !== '') {
    status = 'renewing'
  }

  if (
    policy.renewalStatus === 'manual' &&
    hasRenewal) {
    status = 'expiring'
  }

  if (isFutureDate) {
    status = 'upcoming'
  }

  if (policy.status === 'cancelled') {
    status = 'cancelled'
  }

  switch (status) {
    case 'expired':
      coverText = (<span>{'Cover ended ' + expiryDateStr}</span>)
      iconColor = 'grey'
      break;
    case 'cancelled':
      coverText = (<span>{'Cover ended'}</span>)
      iconColor = 'grey'
      break;
    case 'expiring':
      coverText = (<span style={{ color: '#cc0133' }}>{coverEndsText}</span>)
      iconColor = 'red';
      break;
    case 'renewing':
      coverText = (<span style={{ color: '#0c8a19' }}>{coverRenewsText}</span>)
      iconColor = 'green'
      break;
    case 'upcoming':
      coverText = (<span>{`Cover starts ${startDateStr}`}</span>)
      iconColor = 'blue'
      break;
  }

  const renderPolicyDetails = () => {
    actions.push(`/account/policies/${policy.id}`)
  }

  const renderPolicyDocuments = () => {
    actions.push(`/account/policies/${policy.id}/documents`)
  }

  const renderHolidayChecklist = () => {
    actions.push(`/account/policies/${policy.id}/checklist`)
  }

  const renderCarousel = () => {

    const upsellData  = find(mta.upsellData, ['policyId', policy.id]);

    if (isExpired || isCancelled) {
      return (
        <Repurchase id={policy.id}/>
      )
    }

    if (hasRenewal) {
      return (
        <RenewalPolicy policyId={policy.id} renewalStatus={policy.renewalStatus}/>
      )
    }

    return (
      <>
        <PolicyCarousel
          push={actions.push}
          policy={policy}
          status={status}
          mtaActions={actions.mta}
          upsellData={upsellData}
          isSingle={!isAnnual}
        />
      </>
    )
  }

  return (
    <div className={'policy-card-container'}>

      <div className={'p-card-header'}>
        <div className={'p-card-icon'}>
          <IconCircle iconName={isAnnual ? 'plane-annual.svg' : 'Plane.svg'} color={iconColor}/>
        </div>
        <div style={{ width: '100%' }}>
          <div className={'p-card-h-text-container'}>
            <div className={'p-card-h-text'}>{tripDefinition}</div>
            <div className={'p-card-h-badge'}><StatusBadge status={status}/></div>
          </div>
          <div className={'p-card-destination'}>Policy Ref: {policy.policyNumber}</div>

        </div>
      </div>

      <div className={'p-card-body-container'}>
        <div className={'p-card-body'}>
          <div className={'p-properties'}>
            <div className={'prop-item'}>
              <AdmIcons name={'Calendar'} color={'blue'}/>
              <div className={'prop-item-text'}>{coverText}</div>
            </div>
            <div className={'prop-item'}>
              <AdmIcons name={'People'} color={'blue'}/>
              <div className={'prop-item-text'}>{travellerNumber + ' Traveller(s)'}</div>
            </div>
            <div className={'prop-item'}>
              <AdmIcons name={'ShieldTicked'} color={coverLevel}/>
              <div className={'prop-item-text'}>{capitalize(coverLevel) + ' Cover'}</div>
            </div>
            <div className={'prop-item'}>
              <AdmIcons name={'ShieldPlus'} color={'blue'}/>
              <div
                className={'prop-item-text'}>{addedOptionNumber >= 1 ? `${addedOptionNumber} Optional Extras` : 'No Optional Extras'}</div>
            </div>

            <div className={'p-buttons-container'}>
              <div style={{ flex: 1, textAlign: 'center', marginRight: '10px' }}>
                <Button
                  className={'admiral-white-btn cancel'}
                  bsSize="small"
                  type={'button'}
                  block={true}
                  onClick={renderPolicyDetails}
                >
                  POLICY DETAILS
                </Button>
              </div>
              <div style={{ flex: 1, textAlign: 'center', marginLeft: '10px' }}>
                <Button
                  className={'admiral-white-btn cancel'}
                  bsSize="small"
                  block={true}
                  type={'button'}
                  onClick={renderPolicyDocuments}
                >
                  DOCUMENTS
                </Button>
              </div>
            </div>
            <div className={'p-buttons-container'}>
              <div style={{ flex: 0.5, textAlign: 'center', maxWidth: '206px'}}>
                <Button
                  className={'admiral-white-btn cancel holiday-checklist-button'}
                  bsSize="small"
                  block={true}
                  type={'button'}
                  onClick={renderHolidayChecklist}
                  style={{ padding: '5px !important' }}
                >
                  <img src={require('../../../../Quote/img/green-tick.svg')} alt={''} style={{ marginRight: '3px' }}/>
                  HOLIDAY CHECKLIST
                </Button>
              </div>
            </div>
          </div>
          <div className={'p-make-a-change'}>
            {renderCarousel()}
          </div>
        </div>
      </div>

    </div>
  )
}

export default flow([
  connect(
    (state: IStore) => {
      return {
        auth: state.auth,
        mta: state.mta,
      };
    },
    (dispatch) => ({
      actions: {
        dispatch,
        push: bindActionCreators(push, dispatch),
        mta: bindActionCreators({ ...mtaActions }, dispatch),
      }
    }),
  ),
])(PolicyCard);
